import { api } from "@/lib/http";

export type GatewayKeyEntity = {
  id: number;
  name: string;
  policy_mode: string;
};

export type GatewayKeyItem = {
  id: string;
  name: string;
  description: string | null;
  status: "active" | "revoked";
  revoked_at: string | null;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  providers: Record<string, string[]>;
  entities: Array<GatewayKeyEntity>;
};

export type GatewayKeysListResponse = {
  count: number;
  items: Array<GatewayKeyItem>;
  next_page: number | null;
  previous_page: number | null;
};

export type GatewayKeyGuardrailAction = "block" | "flag" | "redact";

export type GatewayKeyGuardrailItem = {
  id: string;
  key: string;
  feature_id: number;
  display_name: string;
  description: string;
  enabled: boolean;
  action: GatewayKeyGuardrailAction;
  apply_to: string;
  settings: Record<string, unknown>;
  supported_actions: Array<GatewayKeyGuardrailAction>;
  supports_spans: boolean;
  supports_output_direction: boolean;
  settings_schema: {
    type: "object";
    properties?: Record<string, unknown>;
    required?: Array<string>;
  };
  typical_latency_ms: number | null;
};

export type GatewayKeyGuardrailsResponse = {
  gateway_key_id: string;
  guardrails_updated_at: string;
  items: Array<GatewayKeyGuardrailItem>;
};

export type GatewayKeyGuardrailUpdateItem = {
  feature_id: number;
  is_enabled: boolean;
  action: GatewayKeyGuardrailAction;
  apply_to: string;
  settings: Record<string, unknown>;
};

function listActive(page: number, perPage = 10) {
  return api.raw
    .get("api/v1/my-gateway_keys/", {
      searchParams: { page, per_page: perPage, status: "active" },
    })
    .json<GatewayKeysListResponse>();
}

function listRevoked(page: number, perPage = 10) {
  return api.raw
    .get("api/v1/my-gateway_keys/", {
      searchParams: { page, per_page: perPage, status: "revoked" },
    })
    .json<GatewayKeysListResponse>();
}

function revoke(id: string) {
  return api.raw.post(`api/v1/my-gateway_keys/${id}/revoke`);
}

function rename(id: string, name: string) {
  return api.raw
    .patch(`api/v1/my-gateway_keys/${id}`, { json: { name } })
    .json<GatewayKeyItem>();
}

function getGuardrails(id: string) {
  return api.raw
    .get(`api/v1/my-gateway_keys/${id}/guardrails`)
    .json<GatewayKeyGuardrailsResponse>();
}

function updateGuardrails(
  id: string,
  items: Array<GatewayKeyGuardrailUpdateItem>,
) {
  return api.raw
    .put(`api/v1/my-gateway_keys/${id}/guardrails`, { json: items })
    .json<GatewayKeyGuardrailsResponse>();
}

export default {
  listActive,
  listRevoked,
  revoke,
  rename,
  getGuardrails,
  updateGuardrails,
};
